'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import { analytics } from '@/lib/analytics';
import Link from 'next/link';

export default function HeroSection() {
  const [trackingNumber, setTrackingNumber] = useState('');
  const router = useRouter();

  const handleTrack = () => {
    if (trackingNumber?.trim()) {
      analytics?.trackShipmentSearch(trackingNumber);
      analytics?.trackUserAction('hero_search', { trackingNumber });
      router?.push(`/track-shipment?tracking=${trackingNumber}`);
    }
  };

  return (
    <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-700 overflow-hidden">
      {/* Shipping Equipment Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODAiIGhlaWdodD0iODAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9InNoaXBwaW5nIiB3aWR0aD0iODAiIGhlaWdodD0iODAiIHBhdHRlcm5Vbml0cz0idXNlclNwYWNlT25Vc2UiPjxyZWN0IHg9IjEwIiB5PSIxMCIgd2lkdGg9IjIwIiBoZWlnaHQ9IjIwIiBmaWxsPSJub25lIiBzdHJva2U9IndoaXRlIiBzdHJva2Utd2lkdGg9IjEiLz48cmVjdCB4PSI1MCIgeT0iNTAiIHdpZHRoPSIyMCIgaGVpZ2h0PSIyMCIgZmlsbD0ibm9uZSIgc3Ryb2tlPSJ3aGl0ZSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI3NoaXBwaW5nKSIvPjwvc3ZnPg==')] bg-repeat"></div>
      </div>
      
      {/* Gradient Overlays */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-20 left-10 w-96 h-96 bg-blue-500 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-emerald-500 rounded-full blur-3xl"></div>
      </div>
      
      <div className="max-w-7xl mx-auto relative z-10">
        <div className="text-center max-w-4xl mx-auto">
          {/* Professional Badge */}
          <div className="inline-flex items-center gap-2 px-5 py-2 bg-white/10 backdrop-blur-sm rounded-full border border-white/20 mb-6 animate-fade-in-up">
            <Icon name="public/assets/images/ChatGPT_Image_Feb_7_2026_02_48_47_AM-1770428942096.png" size={18} className="text-white" />
            <span className="text-sm font-medium text-white">Professional Consignment & Logistics Solutions</span>
          </div>

          {/* Headline */}
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-heading font-bold text-white leading-tight mb-6 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
            Global Consignment <br />
            <span className="text-primary">& Logistics Excellence</span>
          </h1>

          {/* Subheadline */}
          <p className="text-lg md:text-xl text-slate-300 leading-relaxed mb-10 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
            Complete logistics solutions from pickup to delivery. Track every stop, manage consignments, and ship with confidence across 150+ countries.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-10 animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
            <Link
              href="/order"
              className="px-8 py-4 bg-primary text-primary-foreground rounded-lg font-semibold hover:bg-primary/90 transition-all hover:scale-105 flex items-center justify-center gap-2 shadow-xl"
            >
              <Icon name="public/assets/images/ChatGPT_Image_Feb_7_2026_02_48_47_AM-1770428942096.png" size={20} />
              <span>Create Shipment</span>
            </Link>
            <Link
              href="/products"
              className="px-8 py-4 bg-white/10 backdrop-blur-sm text-white border border-white/20 rounded-lg font-semibold hover:bg-white/20 transition-all hover:scale-105 flex items-center justify-center gap-2"
            >
              <Icon name="CubeIcon" size={20} />
              <span>View Services</span>
            </Link>
          </div>

          {/* Tracking Input */}
          <div className="max-w-2xl mx-auto mb-8 animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
            <div className="flex flex-col sm:flex-row gap-3 p-2 bg-white/95 backdrop-blur-sm rounded-xl shadow-2xl border border-white/20">
              <div className="flex-1 relative">
                <Icon name="MagnifyingGlassIcon" size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" />
                <input
                  type="text"
                  value={trackingNumber}
                  onChange={(e) => setTrackingNumber(e?.target?.value)}
                  onKeyPress={(e) => e?.key === 'Enter' && handleTrack()}
                  placeholder="Enter tracking number (e.g., TRK123456789)"
                  className="w-full pl-12 pr-4 py-4 text-slate-900 placeholder:text-slate-500 focus:outline-none rounded-lg bg-transparent"
                />
              </div>
              <button
                onClick={handleTrack}
                className="px-8 py-4 bg-slate-800 text-white rounded-lg font-medium hover:bg-slate-700 transition-all hover:scale-105 flex items-center justify-center gap-2"
              >
                <span>Track Shipment</span>
                <Icon name="ArrowRightIcon" size={20} />
              </button>
            </div>
          </div>

          {/* Professional Trust Indicators */}
          <div className="flex flex-wrap items-center justify-center gap-8 text-sm text-slate-300 animate-fade-in-up" style={{ animationDelay: '0.5s' }}>
            <div className="flex items-center gap-2">
              <Icon name="CheckCircleIcon" size={20} className="text-emerald-400" />
              <span>ISO 9001 Certified</span>
            </div>
            <div className="flex items-center gap-2">
              <Icon name="CheckCircleIcon" size={20} className="text-emerald-400" />
              <span>150+ Countries</span>
            </div>
            <div className="flex items-center gap-2">
              <Icon name="CheckCircleIcon" size={20} className="text-emerald-400" />
              <span>Multi-Stop Tracking</span>
            </div>
            <div className="flex items-center gap-2">
              <Icon name="CheckCircleIcon" size={20} className="text-emerald-400" />
              <span>24/7 Support</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}